import { useState } from 'react';
import useSWR from 'swr';
import Card from 'components/card';
import {
  ReadPermissionGate,
  UpdatePermissionGate,
} from 'components/PermissionGate';
import { PERMISSION_RESOURCES } from 'constants/permissions';
import { rebalanceCenterService } from 'views/services/rebalanceCenterService';
import { IRebalanceRow } from 'views/types/rebalanceCenter';
import { formatTokenAmount } from 'utils/amountUtils';

const PAGE_SIZE = 20;

const headerClass =
  'border-b border-gray-200 pb-2 pr-3 text-start text-xs font-bold uppercase text-gray-600 dark:border-white/20 dark:text-white';
const cellClass = 'py-2 pr-3 text-sm text-navy-700 dark:text-white';

const shortId = (id: string) => `${id.slice(0, 8)}…`;

const statusClass = (status: string) => {
  const s = status.toLowerCase();
  if (s === 'completed') return 'text-green-600';
  if (s === 'failed') return 'text-red-500';
  return 'text-amber-500';
};

export const RebalanceCenter = () => {
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(0);
  const [busyId, setBusyId] = useState<string | null>(null);

  const { data, isLoading, mutate } = useSWR(
    ['/api/admin/rebalance-transfers', status, page],
    () =>
      rebalanceCenterService.getRebalances({
        status: status || undefined,
        page,
        pageSize: PAGE_SIZE,
      }),
    { refreshInterval: 5000 }
  );

  const rows: IRebalanceRow[] = data?.data ?? [];
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const onRetry = async (id: string) => {
    try {
      setBusyId(id);
      await rebalanceCenterService.retry(id);
      await mutate();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <ReadPermissionGate
      resource={PERMISSION_RESOURCES.ORDER_QUEUE}
      fallback={
        <div className="mt-5 text-center text-gray-600 dark:text-white">
          You don't have access to the Rebalance Center page.
        </div>
      }
    >
      <div className="mt-3">
        <Card extra="p-4">
          {/* Filters */}
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <select
              className="rounded-md border border-gray-200 bg-white px-3 py-2 text-sm dark:bg-navy-800 dark:text-white"
              value={status}
              onChange={e => {
                setStatus(e.target.value);
                setPage(0);
              }}
            >
              <option value="">All statuses</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
            </select>
            {isLoading && (
              <span className="text-sm text-gray-500">Loading…</span>
            )}
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className={headerClass}>From</th>
                  <th className={headerClass}>To</th>
                  <th className={headerClass}>Reason</th>
                  <th className={headerClass}>Asset</th>
                  <th className={headerClass}>Amount</th>
                  <th className={headerClass}>Status</th>
                  <th className={headerClass}>Tx Hash</th>
                  <th className={headerClass}>Error</th>
                  <th className={headerClass}>Created</th>
                  <th className={headerClass}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(row => (
                  <tr
                    key={row.id}
                    className={row.error ? 'bg-red-50 dark:bg-red-900/20' : ''}
                  >
                    <td className={cellClass}>{row.from}</td>
                    <td className={cellClass}>{row.to}</td>
                    <td className={cellClass}>{row.reason}</td>
                    <td className={cellClass}>{row.asset}</td>
                    <td className={cellClass}>
                      {formatTokenAmount(row.amount, row.decimals)}
                    </td>
                    <td className={`${cellClass} ${statusClass(row.status)}`}>
                      {row.status}
                    </td>
                    <td
                      className={`${cellClass} max-w-[160px] truncate font-mono`}
                      title={row.txHash ?? ''}
                    >
                      {row.txHash ? shortId(row.txHash) : '—'}
                    </td>
                    <td
                      className={`${cellClass} max-w-[220px] truncate text-red-500`}
                      title={row.error ?? ''}
                    >
                      {row.error ?? '—'}
                    </td>
                    <td className={cellClass}>
                      {new Date(row.createdAt).toLocaleString()}
                    </td>
                    <td className={cellClass}>
                      <UpdatePermissionGate
                        resource={PERMISSION_RESOURCES.ORDER_QUEUE}
                      >
                        <button
                          disabled={busyId === row.id}
                          onClick={() => onRetry(row.id)}
                          className="rounded bg-brand-500 px-2 py-1 text-xs text-white disabled:opacity-50"
                        >
                          Retry
                        </button>
                      </UpdatePermissionGate>
                    </td>
                  </tr>
                ))}
                {!isLoading && rows.length === 0 && (
                  <tr>
                    <td
                      className="py-4 text-center text-sm text-gray-500"
                      colSpan={10}
                    >
                      No rebalance transfers.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="mt-4 flex items-center justify-between">
            <span className="text-sm text-gray-600 dark:text-white">
              {total} transfer(s) · page {page + 1} / {pageCount}
            </span>
            <div className="flex gap-2">
              <button
                disabled={page <= 0}
                onClick={() => setPage(p => Math.max(0, p - 1))}
                className="rounded border border-gray-200 px-3 py-1 text-sm disabled:opacity-50 dark:text-white"
              >
                Prev
              </button>
              <button
                disabled={page + 1 >= pageCount}
                onClick={() => setPage(p => p + 1)}
                className="rounded border border-gray-200 px-3 py-1 text-sm disabled:opacity-50 dark:text-white"
              >
                Next
              </button>
            </div>
          </div>
        </Card>
      </div>
    </ReadPermissionGate>
  );
};

export default RebalanceCenter;
